import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { matchingPasswords } from '../../../services/app-validators';
import { AuthenticationService } from '../../../services/authentication.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Auth } from '../../../classes/app.models';
import { ApiService } from '../../../services/api.service';
import { ModalService } from '../../../services/modal.service';
import { RecaptchaService } from '../../../services/recaptcha.service';
import { environment } from 'src/environments/environment';

declare let gtag_report_conversion: Function;

type AuthMode = 'otp' | 'password';

/**
 * Where a completed sign-in or sign-up lands.
 *
 * Named once because four flows reach it — password login, password register,
 * email-OTP login and email-OTP register — and they used to disagree: the two
 * register paths went to '/', and the two login paths navigated nowhere at all,
 * leaving the customer on whatever page they happened to open the modal from.
 */
const POST_AUTH_PATH = '/ai';

@Component({
    selector: 'indiapicture-login-modal',
    templateUrl: './login-modal.component.html',
    styleUrls: ['./login-modal.component.scss']
})

export class LoginModalComponent implements OnInit, OnDestroy {
    loginForm: FormGroup;
    registerForm: FormGroup;
    enable = false;
    submitted: boolean;
    loaded = true;

    /**
     * Whether Google and email-OTP sign-in are offered at all.
     *
     * Off hides the Google button, the OTP/Password switch and both OTP panes,
     * leaving email + password. The flows themselves are untouched — this is a
     * visibility switch, so turning it back on needs no code change.
     */
    readonly socialAndOtpAuth = environment.socialAndOtpAuthEnabled;

    // --- Email-OTP flow ----------------------------------------------------
    // Backed by /auth/email/{register,login}/{send,verify}-otp. A verified code
    // returns a full session, so verify closes the modal -- there is no separate
    // sign-in call after signing up.
    //
    // These MUST follow the flag, not default to 'otp' unconditionally: the
    // password forms render on `mode === 'password'`, so with OTP hidden and the
    // mode left at 'otp' the pane would come up completely empty — no fields, no
    // way in, and no error to explain it.
    loginMode: AuthMode = environment.socialAndOtpAuthEnabled ? 'otp' : 'password';
    registerMode: AuthMode = environment.socialAndOtpAuthEnabled ? 'otp' : 'password';

    loginOtpStep = 1;       // 1 = enter email, 2 = enter code
    registerOtpStep = 1;

    loginOtpForm: FormGroup;
    loginOtpCodeForm: FormGroup;
    registerOtpForm: FormGroup;
    registerOtpCodeForm: FormGroup;

    otpSending = false;
    otpVerifying = false;

    // Seconds left before "Resend code" becomes clickable again.
    loginResendIn = 0;
    registerResendIn = 0;

    private loginResendTimer: any;
    private registerResendTimer: any;

    constructor(public formBuilder: FormBuilder, private router: Router, private apiService: ApiService,
        private authService: AuthenticationService, private toastrService: ToastrService, private modalService: ModalService,
        private recaptcha: RecaptchaService,
        // The dialog this was opened in by ModalService.showLoginModal(); null if
        // the component is ever rendered outside a modal.
        @Optional() private activeModal: NgbActiveModal) {
    }

    ngOnInit() {
        /*
         * Start fetching Google's reCAPTCHA v3 script as the modal is built.
         * The token itself is taken per submission inside AuthenticationService
         * — tokens are single-use and expire in about two minutes, so one
         * grabbed here would often be stale by the time the form is sent. This
         * only warms the script so the submit click does not wait on a download.
         */
        this.recaptcha.preload();

        this.loginForm = this.formBuilder.group({
            'email': ['', Validators.compose([Validators.required])],
            'password': ['', Validators.compose([Validators.required, Validators.minLength(5)])]
        });

        this.registerForm = this.formBuilder.group({
            name: ['', Validators.compose([Validators.required, Validators.minLength(3)])],
            email: ['', Validators.compose([Validators.required])],
            phone: ['', Validators.compose([Validators.required])],
            password: ['', Validators.required],
            confirmPassword: ['', Validators.required],
            // captchaResponse: ['', Validators.required]
            policy: ['', Validators.required],
            // Marketing opt-in. Optional by design, so no validator: an unticked
            // box must not block the form. UI only for now — the value is held
            // here but nothing reads it, so nothing is sent to the backend yet.
            promos: [false]
        }, { validator: matchingPasswords('password', 'confirmPassword') });
        this.enable = false;

        const otpCode = () => this.formBuilder.control('', Validators.compose([
            Validators.required, Validators.pattern(/^[0-9]{6}$/)
        ]));

        this.loginOtpForm = this.formBuilder.group({
            email: ['', Validators.compose([Validators.required, Validators.email])]
        });
        this.loginOtpCodeForm = this.formBuilder.group({ code: otpCode() });

        this.registerOtpForm = this.formBuilder.group({
            email: ['', Validators.compose([Validators.required, Validators.email])],
            // As registerForm.promos: captured, optional, not yet sent.
            promos: [false]
        });
        this.registerOtpCodeForm = this.formBuilder.group({ code: otpCode() });
    }

    ngOnDestroy() {
        clearInterval(this.loginResendTimer);
        clearInterval(this.registerResendTimer);
    }

    // --- Mode toggle ------------------------------------------------------

    setLoginMode(mode: AuthMode): void {
        this.loginMode = mode;
    }

    setRegisterMode(mode: AuthMode): void {
        this.registerMode = mode;
    }

    // --- Google -------------------------------------------------------------

    /*
     * A full page navigation, not an XHR: Google's consent screen refuses to
     * render in a frame or an ajax response, so the browser itself has to go to
     * the backend, which redirects on to Google and later back to
     * /auth/callback with the session. One endpoint serves sign in and sign up
     * -- the backend registers the address if it has not seen it before.
     */
    continueWithGoogle(): void {
        window.location.href = this.apiService.googleRedirectUrl;
    }

    // --- Login via OTP ------------------------------------------------------

    sendLoginOtp(): void {
        if (this.loginOtpForm.invalid) {
            this.loginOtpForm.markAllAsTouched();
            return;
        }
        if (this.otpSending) {
            return;
        }

        this.otpSending = true;
        this.authService.sendLoginEmailOtp(this.loginOtpForm.value.email).then(result => {
            this.otpSending = false;

            if (result.success) {
                this.loginOtpStep = 2;
                this.startResendCountdown('login');
                this.toastrService.success(result.message);
            } else {
                /* 404 here means "no account with this email" -- the copy comes
                 * from the backend, so it already points them at Register. */
                this.toastrService.error(result.message);
            }
        });
    }

    verifyLoginOtp(): void {
        if (this.loginOtpCodeForm.invalid) {
            this.loginOtpCodeForm.markAllAsTouched();
            return;
        }
        if (this.otpVerifying) {
            return;
        }

        this.otpVerifying = true;
        this.authService.verifyLoginEmailOtp(
            this.loginOtpForm.value.email,
            this.loginOtpCodeForm.value.code
        ).then(result => {
            this.otpVerifying = false;

            if (result.success) {
                this.toastrService.success(result.message);
                this.closeModal();
                this.router.navigate([POST_AUTH_PATH]);
            } else {
                this.toastrService.error(result.message);
            }
        });
    }

    editLoginEmail(): void {
        this.loginOtpStep = 1;
        this.loginOtpCodeForm.reset();
        clearInterval(this.loginResendTimer);
        this.loginResendIn = 0;
    }

    /* Same endpoint as the first send -- the countdown is the only difference. */
    resendLoginOtp(): void {
        if (this.loginResendIn > 0) {
            return;
        }
        this.sendLoginOtp();
    }

    // --- Register via OTP ---------------------------------------------------

    sendRegisterOtp(): void {
        if (this.registerOtpForm.invalid) {
            this.registerOtpForm.markAllAsTouched();
            return;
        }
        if (this.otpSending) {
            return;
        }

        this.otpSending = true;
        this.authService.sendRegisterEmailOtp(this.registerOtpForm.value.email).then(result => {
            this.otpSending = false;

            if (result.success) {
                this.registerOtpStep = 2;
                this.startResendCountdown('register');
                this.toastrService.success(result.message);
            } else {
                /* 409 here means the address already has an account; the backend
                 * copy tells them to log in instead. */
                this.toastrService.error(result.message);
            }
        });
    }

    verifyRegisterOtp(): void {
        if (this.registerOtpCodeForm.invalid) {
            this.registerOtpCodeForm.markAllAsTouched();
            return;
        }
        if (this.otpVerifying) {
            return;
        }

        this.otpVerifying = true;
        this.authService.verifyRegisterEmailOtp(
            this.registerOtpForm.value.email,
            this.registerOtpCodeForm.value.code
        ).then(result => {
            this.otpVerifying = false;

            if (result.success) {
                /* Verify creates the account and returns a session in one call,
                 * so there is no follow-up sign-in here -- unlike the password
                 * register flow, which registers and then logs in. */
                this.saveMarketingOptIn(
                    this.registerOtpForm.value.email,
                    this.registerOtpForm.value.promos
                );
                this.toastrService.success(result.message);
                this.closeModal();
                this.router.navigate([POST_AUTH_PATH]);
            } else {
                this.toastrService.error(result.message);
            }
        });
    }

    editRegisterEmail(): void {
        this.registerOtpStep = 1;
        this.registerOtpCodeForm.reset();
        clearInterval(this.registerResendTimer);
        this.registerResendIn = 0;
    }

    resendRegisterOtp(): void {
        if (this.registerResendIn > 0) {
            return;
        }
        this.sendRegisterOtp();
    }

    private startResendCountdown(context: 'login' | 'register'): void {
        const isLogin = context === 'login';
        clearInterval(isLogin ? this.loginResendTimer : this.registerResendTimer);

        if (isLogin) {
            this.loginResendIn = 30;
        } else {
            this.registerResendIn = 30;
        }

        const timer = setInterval(() => {
            if (isLogin) {
                this.loginResendIn--;
                if (this.loginResendIn <= 0) {
                    clearInterval(this.loginResendTimer);
                }
            } else {
                this.registerResendIn--;
                if (this.registerResendIn <= 0) {
                    clearInterval(this.registerResendTimer);
                }
            }
        }, 1000);

        if (isLogin) {
            this.loginResendTimer = timer;
        } else {
            this.registerResendTimer = timer;
        }
    }

    // --- Existing email + password flows (unchanged) ----------------------

    public onLoginFormSubmit(): void {
        if (this.loginForm.valid) {
            this.loaded = false;
            this.authService.login(this.loginForm.value['email'], this.loginForm.value['password']).then(resolve => {
                if (resolve) {
                    this.toastrService.success('User login to account successfully');
                    this.closeModal();
                    this.router.navigate([POST_AUTH_PATH]);
                } else {
                    // The backend's own message when it sent one — a rejected
                    // captcha never reached the password check, so blaming the
                    // password would be wrong and unhelpful.
                    this.toastrService.error(this.authService.lastAuthMessage || 'Incorrect login id/pass');
                }
                this.loaded = true;
            });
        }
    }

    /**
     * Put the address on the newsletter list, when the opt-in was ticked.
     *
     * Fire and forget, on purpose. The account exists by the time this runs, so
     * a mailing-list write that fails must not fail the sign-up, hold the modal
     * open, or show an error about something opted into as an afterthought. It
     * is logged and left.
     *
     * Takes the flag rather than reading a form, because the two register modes
     * keep it on different FormGroups.
     */
    private saveMarketingOptIn(email: string, optedIn: any): void {
        const address = (email || '').trim();

        if (!optedIn || !address) { return; }

        this.apiService.addSubscribe(address).subscribe(
            () => { },
            (err: any) => console.error('newsletter opt-in could not be saved', err)
        );
    }

    /**
     * Close the dialog after a successful sign-in or sign-up.
     *
     * Through the modal's own handle. This used to fake a click on the
     * .login-modal backdrop, but ng-bootstrap 10 only dismisses on a real
     * mousedown + mouseup on the backdrop, so the synthetic click did nothing and
     * the dialog stayed open over a signed-in page. dismiss() (not close()) so the
     * options' beforeDismiss still runs its cleanup, exactly as a backdrop click.
     */
    closeModal() {
        if (this.activeModal) {
            this.activeModal.dismiss('signed-in');
            return;
        }

        const modal = document.querySelector('.login-modal') as HTMLElement;
        if (modal) {
            modal.click();
        }
    }

    public onRegisterFormSubmit(values: Auth): void {
        if (this.registerForm.valid) {
            // gtag_report_conversion();
            this.authService.register(values.name, values.email, values.phone, values.password).then(resolve => {
                if (resolve) {
                    this.authService.login(values.email, values.password).then(resolve1 => {
                        if (resolve1) {
                            this.saveMarketingOptIn(values.email, this.registerForm.value.promos);
                            this.toastrService.success('You have successfully signed up. ');
                            this.closeModal();
                            this.router.navigate([POST_AUTH_PATH]);
                        } else {
                            /*
                             * The account exists now, so this must say so rather
                             * than say nothing. It used to have no else at all —
                             * a failed follow-up sign-in left the modal open,
                             * silent, looking as though nothing had happened,
                             * and a second attempt then reported the address as
                             * already taken.
                             */
                            this.toastrService.error(this.authService.lastAuthMessage
                                || 'Account created, but signing in failed. Please sign in.');
                        }
                    });

                } else {
                    // As in onLoginFormSubmit(): a rejected captcha is not a
                    // duplicate address.
                    this.toastrService.error(this.authService.lastAuthMessage || 'Email id already exist');
                }
            }
            );

            // this.snackBar.open('You registered successfully!', '×', { panelClass: 'success', verticalPosition: 'top', duration: 3000 });
        }
        else {
            this.enable = true;
        }
    }

    /*
     * The reCAPTCHA v2 handlers that used to sit here — resolved() and
     * sendTokenToBackend(), feeding an `isTokenValid` flag — are gone. They went
     * with the commented-out <re-captcha> widget in the template: v2 asked the
     * visitor to prove themselves in a checkbox and let the BROWSER decide
     * whether the form could go, which a bot skipping the widget simply ignored.
     * v3 has no widget and nothing to decide here; the token is attached inside
     * AuthenticationService and judged on the server.
     */

    showForgotPasswordModal(event: Event): void {
        event.preventDefault();
        this.modalService.showForgotPasswordModal();
    }

    showPremiumLoginModal(event: Event): void {
        event.preventDefault();
        this.modalService.closeCashBack();
        this.modalService.showPremiumLoginModal();
        // this.modalService.showLoginModal();

    }

}
